import { useTranslation } from "react-i18next"

import { CrewSwitcher, type CrewSwitcherStat } from "@/components/crew-switcher"
import { MomentumRingAvatar } from "@/components/rank/momentum-ring-avatar"
import { RankPill } from "@/components/rank/rank-pill"
import { formatCompactNumber } from "@/lib/format-compact-number"
import { formatMonthYear } from "@/lib/format-month-year"
import { cn } from "@/lib/utils"
import type { RankLadderNode, RankWithPrivileges } from "@/types/rank"

/**
 * OKS-513 — §11 identity head (card 1).
 *
 * Full-bleed brand cover photo + dark scrim. Top-left: the crew switcher
 * chip (reuses the shipped {@link CrewSwitcher}, enriched with per-crew
 * rank/coins/missions via `crewStats`). Center: `<MomentumRingAvatar>`
 * (full ring + rank patch, real photo — OKS-512). Below: name, "@handle ·
 * on {Brand}'s crew since {date}", a rank chip + "🔥 {N} cheers".
 *
 * # Cover fallback (UX Spec §3 edge)
 *
 * `brandCoverUrl` null → a brand-tinted gradient stands in (never a broken
 * image). The cover is decorative (empty alt — it's a background).
 *
 * # Cheers (PRD §3)
 *
 * The 🔥 cheers count is display-only in this ticket — the "who cheered
 * you" detail screen is a separate ticket (PRD "Out of v1"), so the count
 * does not navigate yet.
 *
 * # Locked testids
 *   - `profile-identity-head` — wrapper
 *   - `profile-identity-cover` — cover layer (carries `data-has-cover`)
 *   - `profile-identity-name` / `-handle` / `-tenure` / `-cheers`
 */
interface ProfileIdentityHeadProps {
  name: string
  avatarUrl: string | null
  handle: string | null
  brandName: string
  brandCoverUrl: string | null
  joinedAt: string
  currentRank: RankWithPrivileges | null
  rankLadder: RankLadderNode[]
  cheersCount: number
  dormant: boolean
  /** Per-crew switcher stats keyed by brand id (§11 enrichment). */
  crewStats: Record<number, CrewSwitcherStat>
}

export function ProfileIdentityHead({
  name,
  avatarUrl,
  handle,
  brandName,
  brandCoverUrl,
  joinedAt,
  currentRank,
  rankLadder,
  cheersCount,
  dormant,
  crewStats,
}: ProfileIdentityHeadProps) {
  const { t, i18n } = useTranslation()

  const tenureDate = formatMonthYear(joinedAt, i18n.language)
  // Blazon position from the named ladder: the node whose name matches the
  // current rank (ladder is ascending, `tier` is 1-based).
  const rankPatch = currentRank
    ? {
        tier:
          rankLadder.find((node) => node.name === currentRank.name)?.tier ?? 1,
        tierCount: rankLadder.length || 1,
        name: currentRank.name,
        // OKS-539 — uploaded blazon (nil → position-tinted default).
        imageUrl: currentRank.image_url,
      }
    : undefined

  return (
    <div
      data-testid="profile-identity-head"
      className="relative overflow-hidden rounded-2xl"
    >
      {/* Cover layer (decorative) + scrim. */}
      <div
        data-testid="profile-identity-cover"
        data-has-cover={brandCoverUrl ? "true" : "false"}
        aria-hidden="true"
        className={cn(
          "absolute inset-0 bg-cover bg-center",
          !brandCoverUrl && "from-primary/30 to-primary/5 bg-gradient-to-b",
        )}
        style={
          brandCoverUrl
            ? { backgroundImage: `url(${brandCoverUrl})` }
            : undefined
        }
      />
      <div className="absolute inset-0 bg-black/45" aria-hidden="true" />

      <div className="relative flex flex-col items-center gap-2 px-4 pt-4 pb-6 text-center text-white">
        <div className="flex w-full justify-start">
          <CrewSwitcher
            crewStats={crewStats}
            className="border-white/30 bg-white/10 text-white hover:bg-white/20"
          />
        </div>

        <MomentumRingAvatar
          src={avatarUrl}
          name={name}
          ring="full"
          rankPatch={rankPatch}
          // OKS-539 — the creator identity head (surface #2) shows the
          // frame-less blazon; leaderboard/feed keep the shield (PRD Q5).
          cornerVariant="blazon"
          size="lg"
          className={cn("mt-1", dormant && "opacity-60")}
        />

        <h1
          data-testid="profile-identity-name"
          className="mt-1 max-w-full truncate text-2xl font-semibold"
        >
          {name}
        </h1>

        <p
          data-testid="profile-identity-tenure"
          className="max-w-full truncate text-sm text-white/80"
        >
          {handle && (
            <span data-testid="profile-identity-handle">@{handle} · </span>
          )}
          {t("xp.profile.identity.tenure", {
            brand: brandName,
            date: tenureDate,
          })}
        </p>

        <div className="mt-1 flex items-center gap-2">
          {currentRank && <RankPill name={currentRank.name} />}
          <span
            data-testid="profile-identity-cheers"
            className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 text-sm font-medium"
          >
            🔥{" "}
            {t("profile.identity.cheers", {
              value: formatCompactNumber(cheersCount),
            })}
          </span>
        </div>
      </div>
    </div>
  )
}
